/**
 * One answer per call at a time.
 *
 * Answering a verto call is several awaits long: the microphone, the SDP exchange, ICE gathering
 * and the `verto.answer` round trip. The vendored client checks that the call is still ringing
 * only before the first of them, so two answers of the same call inside that window each build a
 * peer connection of their own and each send `verto.answer`. The switch takes the second as a new
 * offer and moves the call's audio to it, while the phone goes on playing the first: the call
 * connects and stays silent to the end. The browser's auto-answer and a supervisor's listen-in
 * answering the same call did exactly that (2026-09-24); a double click on Answer can too.
 *
 * So every answer goes through one of these. While an answer of a call is in flight, a second one
 * waits on it instead of starting another. Once it settles the call is answered, or still ringing
 * after a failure, and the next answer is a new attempt.
 *
 * Kept out of `softphone-context.tsx` so the rule is tested without a Verto client or React, and
 * out of `vendor/`, which is ported and not ours to change.
 */
export class AnswerOnce {
  private inFlight = new Map<string, Promise<void>>();

  run(callId: string, answer: () => Promise<void>): Promise<void> {
    const running = this.inFlight.get(callId);
    if (running) return running;
    // Started from a resolved promise so that even an `answer` that throws before its first await
    // settles `attempt` rather than escaping this call, and the cleanup below always runs after
    // `attempt` exists.
    const attempt: Promise<void> = Promise.resolve()
      .then(answer)
      .finally(() => {
        if (this.inFlight.get(callId) === attempt) this.inFlight.delete(callId);
      });
    this.inFlight.set(callId, attempt);
    return attempt;
  }
}
